import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  MapPin,
  Sparkles,
  Lock,
  ShieldCheck,
  Star,
  Heart,
  Phone,
  Wand2,
  Crown,
  Users,
  Mail,
} from "lucide-react";
import wiCities from "@/components/home/wiCities.json";
import { SchoolVerifyModal } from "@/components/SchoolVerifyModal";
import { SignupPromoModal } from "@/components/SignupPromoModal";
import { mpCompleteRegistration } from "@/lib/metaPixel";

const HERO =
  "https://customer-assets.emergentagent.com/job_site-enhancer-58/artifacts/p6rw3yje_Heather%20Login.png";

const PERKS = [
  { icon: Lock, label: "Dress Lock", copy: "Guaranteed one-of-one at your school. No twins on prom night." },
  { icon: Wand2, label: "AI Try-On", copy: "See yourself in any gown in 12 seconds — before you commit." },
  { icon: Crown, label: "Ambassador Perks", copy: "Bronze → Platinum tiers. Earn dress credit, VIP fittings & more." },
  { icon: Users, label: "Squad Planner", copy: "Sync colors with your group. Nobody clashes. Everybody glows." },
];

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = ["signup", "register", "parent"].includes(params.get("mode")) ? "register" : "login";
  const initialRole = params.get("mode") === "parent" || params.get("role") === "parent" ? "parent" : "student";
  // Where to send the shopper after a successful login/register. Guests who
  // hit the auth wall from cart/checkout come here with ?returnTo=/cart —
  // we always send them back so they don't lose their spot.
  const rawReturnTo = params.get("returnTo") || params.get("next") || "";
  const returnTo = rawReturnTo && rawReturnTo.startsWith("/") ? rawReturnTo : "";
  const reason = params.get("reason");
  const [role, setRole] = useState(initialRole);
  const [mode, setMode] = useState(initialMode);
  const isParent = role === "parent";
  const [form, setForm] = useState({ name: "", email: "", password: "", city: "", zip: "" });
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [cityQuery, setCityQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsSchoolEmail, setNeedsSchoolEmail] = useState(null);
  const [sentTo, setSentTo] = useState(null);
  const [schoolName, setSchoolName] = useState(null);
  const [addSchoolOpen, setAddSchoolOpen] = useState(false);
  const [promo, setPromo] = useState(null); // Oconomowoc signup gift modal

  const cityMatches = useMemo(() => {
    if (!cityQuery || cityQuery.length < 2) return [];
    const q = cityQuery.toLowerCase();
    return Object.keys(wiCities).filter((c) => c.toLowerCase().includes(q)).slice(0, 6);
  }, [cityQuery]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back to Heather's");
        navigate(returnTo || "/");
      } else {
        const result = await register(form.name, form.email, form.password, { sms_opt_in: smsOptIn, marketing_opt_in: marketingOptIn, role: isParent ? "parent" : undefined });
        if (isParent) {
          // Parents are auto-signed-in on register (no magic link needed).
          toast.success("Welcome to Heather's — you're all set.");
          // Meta Pixel — CompleteRegistration
          try { mpCompleteRegistration({ method: "parent" }); } catch { /* noop */ }
          navigate(returnTo || "/account");
          return;
        }
        // Student registration auto-sends the school magic link. Route the user
        // to a "check your inbox" state so they know exactly what to do next.
        setSentTo(result?.magic_link_sent_to || form.email);
        setSchoolName(result?.school?.name || null);
        // Oconomowoc promo — 5 free AI Try-On credits (silent everywhere else).
        if (result?.promo) {
          setPromo(result.promo);
          try { window.posthog?.capture("signup_promo_shown", { code: result.promo.code, credits: result.promo.credits }); } catch { /* noop */ }
        }
        // Meta Pixel — CompleteRegistration
        try { mpCompleteRegistration({ method: "student" }); } catch { /* noop */ }
      }
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      const msg = err?.response?.data?.detail?.message;
      if (code === "school_email_required") {
        if (isParent) {
          // Parents don't need a school email — this fires only when the
          // parent's account doesn't exist yet. Nudge them to the invite path.
          toast.error("We don't recognize that email yet. Ask your student to invite you from their account.");
        } else {
          setNeedsSchoolEmail(msg || "You need to sign up with your school email.");
        }
      } else {
        toast.error(errMsg(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-screen bg-ivory">
      <Seo title="Sign In or Join" description="Sign in or create your Heather's Boutique account." path="/auth" noindex />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative w-full overflow-hidden bg-ink">
        <motion.img
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
          src={HERO}
          alt="Heather's Boutique — prom dress editorial"
          className="w-full h-[68vh] md:h-[82vh] object-cover object-top md:object-[center_20%]"
        />
        {/* gradient wash */}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/10 to-ink/80 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/50 via-transparent to-transparent pointer-events-none" />

        {/* top bar */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 md:px-12 py-6 z-10">
          <Link to="/" data-testid="brand-link" className="font-display text-2xl md:text-3xl text-ivory tracking-wide">
            Heather's Boutique
          </Link>
          <a
            href="tel:+18884506525"
            className="hidden md:flex items-center gap-2 text-ivory/90 hover:text-cream font-label text-[11px] tracking-[0.2em]"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> (888) 450-6525
          </a>
        </div>

        {/* headline */}
        <div className="absolute inset-x-0 bottom-0 px-6 md:px-12 pb-10 md:pb-16 z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="max-w-3xl"
          >
            <p className="font-label text-[10px] md:text-[11px] tracking-[0.35em] text-cream mb-4 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> WISCONSIN'S PROM DESTINATION · EST. 2010
            </p>
            <h1 className="font-display text-ivory text-5xl md:text-7xl leading-[0.95] tracking-tight">
              Your gown is <em className="italic text-cream">waiting</em>.<br />
              <span className="text-cream">Lock it before she does.</span>
            </h1>
            <p className="font-body text-ivory/85 text-base md:text-lg mt-6 max-w-xl leading-relaxed">
              Wisconsin's home for prom, homecoming & graduation gowns since 2010 — the only boutique with a real Dress Lock, AI Try-On, and Heather herself on speed dial.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── FORM + STORY ────────────────────────────────────────────────── */}
      <section className="relative -mt-16 md:-mt-24 z-20 px-4 md:px-8 pb-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-6 md:gap-10">

          {/* FORM CARD */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="md:col-span-3 bg-ivory rounded-sm shadow-[0_30px_80px_-30px_rgba(10,10,10,0.35)] border border-cream/60 p-8 md:p-12"
          >
            {/* Role toggle — Student vs Parent. Same form flow, different rules. */}
            <div className="flex gap-2 mb-6" data-testid="auth-role-toggle">
              <button
                type="button"
                data-testid="role-student"
                onClick={() => { setRole("student"); if (mode === "register") setMode("register"); }}
                className={`flex-1 py-3 rounded-sm font-label text-[10px] tracking-[0.28em] transition-colors border ${
                  role === "student"
                    ? "bg-ink text-ivory border-ink"
                    : "bg-transparent text-ink/50 border-cream hover:border-rosemetal hover:text-ink"
                }`}
              >
                I'M A STUDENT
              </button>
              <button
                type="button"
                data-testid="role-parent"
                onClick={() => { setRole("parent"); if (mode === "register") setMode("login"); }}
                className={`flex-1 py-3 rounded-sm font-label text-[10px] tracking-[0.28em] transition-colors border ${
                  role === "parent"
                    ? "bg-rosemetal text-ivory border-rosemetal"
                    : "bg-transparent text-ink/50 border-cream hover:border-rosemetal hover:text-ink"
                }`}
              >
                I'M A PARENT
              </button>
            </div>

            <div className="flex gap-8 mb-8 border-b border-cream pb-4">
              <button
                data-testid="tab-login"
                onClick={() => setMode("login")}
                className={`font-display text-3xl md:text-4xl transition-colors ${
                  mode === "login" ? "text-ink" : "text-ink/25 hover:text-rosemetal"
                }`}
              >
                Sign In
              </button>
              <button
                data-testid="tab-register"
                onClick={() => setMode("register")}
                className={`font-display text-3xl md:text-4xl transition-colors ${
                  mode === "register" ? "text-ink" : "text-ink/25 hover:text-rosemetal"
                }`}
              >
                {isParent ? "Sign Up" : "Join"}
              </button>
            </div>

            {/* Guest-returning-to-checkout banner. Shown only when a page (typ.
                Cart) sent the shopper here with ?returnTo=/cart&reason=checkout. */}
            {returnTo && (
              <div data-testid="auth-return-banner" className="mb-6 border border-rosemetal/30 bg-blush/40 rounded-sm px-4 py-3">
                <p className="font-label text-[9px] tracking-[0.28em] text-rosemetal mb-1">
                  {reason === "checkout" ? "ONE STEP FROM CHECKOUT" : "SIGN IN TO CONTINUE"}
                </p>
                <p className="font-body text-sm text-ink/80">
                  {reason === "checkout"
                    ? "Sign in or create an account to complete your order. Your bag is saved and we'll bring you right back."
                    : "Sign in below and we'll take you back to where you were."}
                </p>
              </div>
            )}

            <p className="font-body text-sm text-ink/60 mb-6">
              {isParent
                ? (mode === "login"
                    ? "Sign in with your parent account — any personal email works."
                    : "Create your parent account — no school email needed. Shop dresses on your own, or link to your student later.")
                : (mode === "login"
                    ? "Welcome back, gorgeous. Your locked looks are waiting."
                    : "Sign up with your school email — first AI try-on is on us.")}
            </p>

            {isParent && mode === "register" && (
              <div data-testid="parent-perks" className="border border-rosemetal/30 bg-blush/25 rounded-sm p-4 mb-5">
                <p className="font-label text-[9px] tracking-widest text-rosemetal mb-1">◉ AS A PARENT YOU CAN</p>
                <ul className="font-body text-xs text-ink/75 space-y-1">
                  <li>◦ Shop and buy dresses for yourself or gifts</li>
                  <li>◦ Get invited by your student to link accounts + buy their gown</li>
                  <li>◦ Gift AI try-on credits so your student can visualize the look (parents can't generate try-ons themselves)</li>
                  <li>◦ View your student's AI lookbook once linked</li>
                </ul>
              </div>
            )}

            {sentTo ? (
              <div data-testid="check-inbox-block" className="border border-rosemetal/40 bg-blush/40 rounded-sm p-6 text-center space-y-3">
                <div className="w-14 h-14 mx-auto rounded-full bg-champagne/40 border border-champagne flex items-center justify-center">
                  <Mail className="w-6 h-6 text-rosemetal" />
                </div>
                <p className="font-label text-[10px] text-rosemetal tracking-widest">MAGIC LINK SENT</p>
                <h3 className="font-display text-3xl text-ink leading-tight">Check your school inbox.</h3>
                <p className="font-body text-sm text-ink/70 leading-relaxed">
                  We just sent a one-time link to <b className="text-ink">{sentTo}</b>{schoolName ? <> at <b className="text-ink">{schoolName}</b></> : null}. Tap it and you're in — no password needed.
                </p>
                <p className="font-label text-[9px] tracking-widest text-ink/50">EXPIRES IN 15 MIN · CHECK SPAM · MAX 3 RESENDS / HOUR</p>
              </div>
            ) : (
            <form onSubmit={submit} className="space-y-5">
              {mode === "register" && (
                <input
                  data-testid="name-input"
                  required
                  placeholder={isParent ? "Parent full name" : "Full name"}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border-b border-cream py-3 outline-none focus:border-rosemetal font-body text-sm bg-transparent transition-colors"
                />
              )}
              <input
                data-testid="email-input"
                required
                type="email"
                placeholder={isParent
                  ? "The email your student invited"
                  : (mode === "register" ? "Your school email (jane@yourschool.k12.wi.us)" : "Your school email")}
                value={form.email}
                onChange={(e) => { setForm({ ...form, email: e.target.value }); setNeedsSchoolEmail(null); }}
                className="w-full border-b border-cream py-3 outline-none focus:border-rosemetal font-body text-sm bg-transparent transition-colors"
              />
              <input
                data-testid="password-input"
                required
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border-b border-cream py-3 outline-none focus:border-rosemetal font-body text-sm bg-transparent transition-colors"
              />

              {mode === "register" && isParent && (
                <>
                  <div className="relative">
                    <div className="flex items-center gap-2 border-b border-cream py-3 focus-within:border-rosemetal">
                      <MapPin className="w-4 h-4 text-ink/50 shrink-0" strokeWidth={1.5} />
                      <input
                        data-testid="wi-city-input"
                        required
                        placeholder="Wisconsin city (start typing…)"
                        value={cityQuery}
                        onChange={(e) => {
                          setCityQuery(e.target.value);
                          setForm({ ...form, city: e.target.value });
                        }}
                        className="w-full outline-none font-body text-sm bg-transparent"
                        autoComplete="off"
                      />
                    </div>
                    {cityMatches.length > 0 && form.city !== cityMatches[0] && (
                      <div
                        data-testid="wi-city-suggestions"
                        className="absolute left-0 right-0 top-full mt-1 bg-ivory border border-cream rounded-sm shadow-lg z-10 max-h-56 overflow-y-auto"
                      >
                        {cityMatches.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => {
                              setCityQuery(c);
                              setForm({ ...form, city: c });
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-blush/50 font-body text-sm border-b border-cream last:border-0"
                          >
                            {c}, WI
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    data-testid="wi-zip-input"
                    required
                    pattern="53\d{3}|54\d{3}|53\d{3}-\d{4}|54\d{3}-\d{4}"
                    title="Please enter a valid Wisconsin ZIP (53000–54999)"
                    placeholder="Wisconsin ZIP code"
                    value={form.zip}
                    onChange={(e) => setForm({ ...form, zip: e.target.value })}
                    className="w-full border-b border-cream py-3 outline-none focus:border-rosemetal font-body text-sm bg-transparent transition-colors"
                  />
                  <p className="font-label text-[9px] text-ink/50 tracking-widest">
                    ◉ WISCONSIN FAMILIES ONLY · WE DELIVER STATEWIDE
                  </p>
                </>
              )}

              {mode === "register" && (
                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="auth-marketing-opt-in-label">
                    <input
                      type="checkbox"
                      data-testid="auth-marketing-opt-in"
                      checked={marketingOptIn}
                      onChange={(e) => setMarketingOptIn(e.target.checked)}
                      className="mt-1 accent-rosemetal w-4 h-4 shrink-0"
                    />
                    <span className="font-body text-xs text-ink/70 leading-snug">
                      Email me new arrivals, member perks, and prom-season drops.
                      Never spam — you can unsubscribe from any email in one tap.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="auth-sms-opt-in-label">
                    <input
                      type="checkbox"
                      data-testid="auth-sms-opt-in"
                      checked={smsOptIn}
                      onChange={(e) => setSmsOptIn(e.target.checked)}
                      className="mt-1 accent-rosemetal w-4 h-4 shrink-0"
                    />
                    <span className="font-body text-xs text-ink/70 leading-snug">
                      Text me updates about my dress lock, order status & prom reminders at the number I provide.
                      Msg &amp; data rates may apply. Reply STOP anytime to opt out.
                      <span className="block text-ink/40 text-[10px] mt-0.5">
                        Consent to marketing texts is not a condition of purchase.
                      </span>
                    </span>
                  </label>
                  <p className="font-body text-[10px] text-ink/50 leading-snug pt-1">
                    By creating an account you agree to our{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-rosemetal underline">Terms</a>{" "}
                    &{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-rosemetal underline">Privacy Policy</a>.
                  </p>
                </div>
              )}

              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                data-testid="auth-submit"
                disabled={busy}
                className="w-full bg-ink text-ivory py-4 font-label text-[11px] tracking-[0.25em] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2 mt-4 disabled:opacity-60"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === "login" ? "SIGN IN" : isParent ? "CREATE PARENT ACCOUNT" : "CREATE ACCOUNT"}
              </motion.button>
              {needsSchoolEmail && (
                <div data-testid="school-email-required-banner" className="mt-4 border border-rosemetal/40 bg-blush/40 rounded-sm p-4">
                  <p className="font-label text-[9px] tracking-widest text-rosemetal mb-1">SCHOOL EMAIL REQUIRED</p>
                  <p className="font-body text-sm text-ink/80 leading-relaxed mb-3">{needsSchoolEmail}</p>
                  <button
                    type="button"
                    data-testid="auth-request-add-school"
                    onClick={() => setAddSchoolOpen(true)}
                    className="font-label text-[10px] tracking-widest text-rosemetal underline underline-offset-2"
                  >
                    MY SCHOOL ISN'T LISTED →
                  </button>
                </div>
              )}
            </form>
            )}

            <div className="mt-8 pt-6 border-t border-cream">
              <p className="font-body text-xs text-ink/60 text-center leading-relaxed">
                {isParent ? (
                  mode === "login" ? (
                    <>
                      New parent?{" "}
                      <button onClick={() => setMode("register")} className="text-rosemetal underline underline-offset-2">
                        Create your account
                      </button>{" "}
                      — no school email needed.
                    </>
                  ) : (
                    <>
                      Already have an account?{" "}
                      <button onClick={() => setMode("login")} className="text-rosemetal underline underline-offset-2">
                        Sign in
                      </button>
                    </>
                  )
                ) : mode === "login" ? (
                  <>
                    New to Heather's?{" "}
                    <button onClick={() => setMode("register")} className="text-rosemetal underline underline-offset-2">
                      Create an account
                    </button>{" "}
                    — it's free.
                  </>
                ) : (
                  <>
                    Registering with a school email unlocks{" "}
                    <span className="text-rosemetal">Dress Locks</span>, Squads & your school portal.
                  </>
                )}
              </p>
            </div>

            {/* trust row */}
            <div className="mt-8 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="font-display text-2xl text-ink">258</div>
                <div className="font-label text-[9px] tracking-[0.2em] text-ink/50 mt-1">WI SCHOOLS SERVED</div>
              </div>
              <div className="border-x border-cream">
                <div className="font-display text-2xl text-ink">15+ yrs</div>
                <div className="font-label text-[9px] tracking-[0.2em] text-ink/50 mt-1">EST. 2010</div>
              </div>
              <div>
                <div className="font-display text-2xl text-ink">5.0★</div>
                <div className="font-label text-[9px] tracking-[0.2em] text-ink/50 mt-1">GOOGLE RATING</div>
              </div>
            </div>
          </motion.div>

          {/* SIDE STORY */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.65, duration: 0.7 }}
            className="md:col-span-2 space-y-6"
          >
            {/* Heather's Story */}
            <div className="bg-cream/60 rounded-sm p-8 border border-cream">
              <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-3">MEET HEATHER</p>
              <h3 className="font-display text-3xl text-ink leading-tight mb-4">
                One boutique. One promise:<br />
                <em className="text-rosemetal">no twins on prom night.</em>
              </h3>
              <p className="font-body text-sm text-ink/70 leading-relaxed mb-4">
                Heather opened her boutique on Brown Street in Oconomowoc with one mission — every Wisconsin girl deserves to feel like the only one in the room. Every gown is hand-picked from designers you won't see on the rack at any chain store, and every fitting is done by a real human who cares whether that neckline sits just right.
              </p>
              <p className="font-body text-sm text-ink/70 leading-relaxed">
                We're not a chain. We're a mom, a small team of stylists, an alterations wizard, and now — a little AI magic to help you find <em>the one</em> before you ever step in the door.
              </p>
              <div className="mt-5 pt-5 border-t border-cream/80 flex items-center gap-3">
                <Heart className="w-4 h-4 text-rosemetal" fill="#B76E79" strokeWidth={0} />
                <p className="font-label text-[10px] tracking-[0.2em] text-ink/60">
                  BROWN ST · SUITE 211 · OCONOMOWOC, WI
                </p>
              </div>
            </div>

            {/* Perks */}
            <div className="grid grid-cols-2 gap-3">
              {PERKS.map((p, i) => (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.08 }}
                  className="bg-ivory border border-cream rounded-sm p-4 hover:border-rosemetal transition-colors"
                >
                  <p.icon className="w-5 h-5 text-rosemetal mb-2" strokeWidth={1.5} />
                  <div className="font-display text-lg text-ink leading-none">{p.label}</div>
                  <p className="font-body text-[11px] text-ink/60 mt-2 leading-snug">{p.copy}</p>
                </motion.div>
              ))}
            </div>

            {/* trust badge */}
            <div className="flex items-center gap-3 bg-ink text-ivory rounded-sm p-4">
              <ShieldCheck className="w-5 h-5 text-cream shrink-0" strokeWidth={1.5} />
              <p className="font-body text-xs leading-snug">
                Your info stays private. We only use it to unlock your school portal & keep track of your locks.
              </p>
            </div>
          </motion.div>
        </div>

        {/* ── BRAND PROMISE ─────────────────────────────────────────── */}
        <div className="max-w-6xl mx-auto mt-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1 bg-cream" />
            <p className="font-label text-[10px] tracking-[0.35em] text-ink/60">THE HEATHER'S PROMISE</p>
            <div className="h-px flex-1 bg-cream" />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: "Real humans, real fittings", copy: "Book a 1-on-1 stylist appointment. No commission games, no upsell — just honest opinions from someone who's dressed thousands of girls." },
              { title: "One dress, one school", copy: "Our Dress Lock guarantees no one else at your school buys the same gown in the same color. Prom night, you'll be the only you." },
              { title: "Made for Wisconsin", copy: "From Hudson to Kenosha, Superior to Racine — we ship, we fit, and we know your prom dates by heart." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-blush/40 border border-cream rounded-sm p-6"
              >
                <Star className="w-4 h-4 text-rosemetal mb-3" fill="#B76E79" strokeWidth={0} />
                <p className="font-display text-xl text-ink italic leading-snug">{item.title}</p>
                <p className="font-body text-sm text-ink/65 mt-3 leading-relaxed">{item.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* footer CTA */}
        <div className="max-w-6xl mx-auto mt-14 text-center">
          <p className="font-body text-sm text-ink/60">
            Questions? Text Heather directly at{" "}
            <a href="tel:+18884506525" className="text-rosemetal font-semibold underline underline-offset-4">
              (888) 450-6525
            </a>{" "}
            — real human, real fast.
          </p>
        </div>
      </section>
      <SchoolVerifyModal
        open={addSchoolOpen}
        reason="unlock"
        onClose={() => setAddSchoolOpen(false)}
      />
      <SignupPromoModal promo={promo} onClose={() => setPromo(null)} />
    </div>
  );
}
